import {OcapiService} from '../ocapi/ocapi.service';
import {filter, map, mergeMap} from 'rxjs/operators';
import {TvShopEntertainmentPack} from '../../model/tv-shop-entertainment-pack';
import {combineLatest, forkJoin, Observable} from 'rxjs';
import {DtvCatalog} from '../../model/ocapi/dtv-catalog';
import {OwnedProduct} from '../../model/sofy/sales-offer/owned-product';
import {Option} from '../../model/sofy/sales-offer/option';
import {TvShopEntertainmentPackMapper} from '../../shared/mappers/tv-shop-entertainment-pack/tv-shop-entertainment-pack-mapper';
import {Injectable} from '@angular/core';
import {SofyService} from '../sofy/sofy.service';
import {OmapiService} from '../omapi/omapi.service';
import {EntertainmentPackUtils} from '../../shared/utils/entertainmentpack/entertainment-pack-utils';
import {OmapiIdConstants} from '../../shared/omapi-id-constants';
import {PromoUtil} from '../../model/sofy/sales-offer/promo.util';
import {PriceDetail} from '../../model/ocapi/price-detail';
import {UserTypeDataService} from '../user-type/user-type.data.service';
import {CustomerProductHoldingProduct, CustomerProductHoldingUtil} from '../../model/customer-product-holding/customer-product-holding.util';

@Injectable()
export class EntertainmentPackService {
  constructor(private sofyService: SofyService,
              private ocapiService: OcapiService,
              private omapiService: OmapiService,
              private userTypeDataService: UserTypeDataService,
              private entertainmentPackUtils: EntertainmentPackUtils) {
  }

  public getEntertainmentPacksAndStreamingServices(dtvIdentifier, isSoho): Observable<{
    tvShopEntertainmentPacks: TvShopEntertainmentPack[],
    streamingServicesOptions: TvShopEntertainmentPack[]
  }> {
    return this.ocapiService.getDtvCatalog()
      .pipe(
        map(eligibleDtvProducts => {
          return this.findCatalog(eligibleDtvProducts.dtvcatalogs, dtvIdentifier);
        }),
        filter(dtvCatalog => !!dtvCatalog),
        mergeMap((dtvCatalog: DtvCatalog) => {
          return this.getAllSofyRequiredProperties()
            .pipe(
              mergeMap(result => {
                const address = this.ocapiService.getAddressFromCustomerProductHolding(result[0], dtvIdentifier);

                return this.sofyService.getSalesOffer(dtvCatalog.omapiid, dtvIdentifier, result[0], result[1], address).pipe(
                  map(salesOffer => {
                    return this.mapToTvShopEntertainmentPacks(salesOffer, dtvCatalog, isSoho);
                  }));
              })
            );
        })
      );
  }

  public enrichTvEntertainmentPacksWithOmapi(tvShopEntertainmentPacks: TvShopEntertainmentPack[]): Observable<TvShopEntertainmentPack[]> {
    const observables = new Array<Observable<TvShopEntertainmentPack>>();
    tvShopEntertainmentPacks.forEach(pack => {
      observables.push(this.omapiService.getPackWithWeightForOmapiId(pack));
    });
    // forkJoin do omapi enrichment in parallel
    return forkJoin(observables).pipe(
      map((entertainmentPacks: Array<TvShopEntertainmentPack>) => {
        this.insertTrialsPackForCarousel(entertainmentPacks);
        this.entertainmentPackUtils.sortDtvCatalogOnWeight(entertainmentPacks);
        return entertainmentPacks;
      }),
      map((entertainmentPacks: Array<TvShopEntertainmentPack>) => {
        return this.updateSubscriptionForDayPassFutureProducts(entertainmentPacks);
      })
    );
  }

  private getAllSofyRequiredProperties() {
    return combineLatest([
      this.ocapiService.getCustomerProductHolding(),
      this.ocapiService.getPromotions()]
    );
  }

  private insertTrialsPackForCarousel(packs) {
    const firstTrial = this.getFirstTrialOnPacks(packs);
    const trialPack = new TvShopEntertainmentPack();
    Object.assign(trialPack, firstTrial);
    trialPack.omapiId = OmapiIdConstants.TRIALS;
    trialPack.subscribed = false;
    packs.push(trialPack);
  }

  private getFirstTrialOnPacks(packs: Array<TvShopEntertainmentPack>): TvShopEntertainmentPack {
    const compareOnOmapiId = (a, b) => a.omapiId < b.omapiId ? -1 : 1;
    return packs.sort(compareOnOmapiId).find(pack => pack.isTrial);
  }

  private mapToTvShopEntertainmentPacks(salesOffer, dtvCatalog, isSoho): any {
    const tvShopEntertainmentPacks: Array<TvShopEntertainmentPack> = [];
    const streamingServicesOptions: Array<TvShopEntertainmentPack> = [];
    const ownedProduct: OwnedProduct = this.getOwnedProductForOmapiId(salesOffer, dtvCatalog);
    if (ownedProduct) {
      dtvCatalog.entertainmentpacks.forEach(entertainmentPack => {
        entertainmentPack.isSoho = isSoho;
        this.enrichWithSofyPrice(ownedProduct, entertainmentPack);
        tvShopEntertainmentPacks.push(TvShopEntertainmentPackMapper.mapEntertainmentPack(entertainmentPack));
      });
      if (ownedProduct.streamingservices) {
        ownedProduct.streamingservices.forEach(streamingservice => {
          this.enrichStreamingServiceWithSofyPrice(ownedProduct, streamingservice);
          streamingServicesOptions.push(TvShopEntertainmentPackMapper.mapOptionToEntertainmentPack(streamingservice));
        });
      }
    }
    return {tvShopEntertainmentPacks, streamingServicesOptions};
  }

  enrichStreamingServicesWithOmapi(
    tvShopEntertainmentPacks: TvShopEntertainmentPack[],
    customerProductHolding: CustomerProductHoldingProduct[]
  ): TvShopEntertainmentPack[] {
    return tvShopEntertainmentPacks.map((streamingService) => {
      if (CustomerProductHoldingUtil.ownsTvShopEntertainmentPack(streamingService, customerProductHolding)) {
        streamingService.subscribed = true;
      }
      return streamingService;
    });
  }

  private enrichWithSofyPrice(ownedProduct: OwnedProduct, entertainmentPack) {
    const foundOption: Option = ownedProduct.options.find(option => option.omapiid === entertainmentPack.omapiid);
    if (foundOption) {
      this.enrichOptionFound(foundOption, entertainmentPack);
    }
  }

  private enrichStreamingServiceWithSofyPrice(ownedProduct: OwnedProduct, entertainmentPack) {
    const foundOption: Option = ownedProduct.streamingservices.find(option => option.omapiid === entertainmentPack.omapiid);
    if (foundOption) {
      this.enrichOptionFound(foundOption, entertainmentPack);
    }
  }

  private enrichOptionFound(foundOption: Option, entertainmentPack: TvShopEntertainmentPack) {
    let displayPrice: number;
    let promoPrice: number;
    const originalPrice = entertainmentPack.isSoho ? foundOption.priceexclvat : foundOption.price;

    if (this.isLessThanAMonthPromoApplicable(foundOption, entertainmentPack)) {
      displayPrice = Number(originalPrice.toFixed(2));
      promoPrice = Number((displayPrice + PromoUtil.getPermanentOrLessThanAMonthDiscount(foundOption)).toFixed(2));
    } else {
      displayPrice = Number((originalPrice + PromoUtil.getPermanentOrLessThanAMonthDiscount(foundOption)).toFixed(2));
      if (entertainmentPack.isSoho) {
        promoPrice = Number((displayPrice + PromoUtil.getMonthlyDiscountForSoho(foundOption)).toFixed(2));
      } else {
        promoPrice = Number((displayPrice + PromoUtil.getMonthlyDiscount(foundOption)).toFixed(2));
      }
    }

    const promoDuration = PromoUtil.getShortestPromoDuration(foundOption);
    const promoLabel = PromoUtil.getPromoLabel(foundOption);
    entertainmentPack.priceDetail = new PriceDetail(displayPrice, promoPrice, promoDuration, promoLabel);
  }

  private isLessThanAMonthPromoApplicable(foundOption: Option, entertainmentPack: any): boolean {
    return PromoUtil.getShortestPromoDuration(foundOption) === 0 && entertainmentPack.daypass;
  }

  private getOwnedProductForOmapiId(salesOffer, dtvCatalog) {
    return salesOffer.ownedproducts.find(product => product.omapiid === dtvCatalog.omapiid);
  }

  private findCatalog(dtvcatalogs: Array<DtvCatalog>, dtvIdentifier: string): DtvCatalog {
    return dtvcatalogs.filter(dtvCatalog => dtvCatalog.dtvidentifier === dtvIdentifier).pop();
  }

  private updateSubscriptionForDayPassFutureProducts(entertainmentPacks: Array<TvShopEntertainmentPack>) {
    const futureProducts = entertainmentPacks
      .filter(entertainmentPack => entertainmentPack.isDayPass === true && entertainmentPack.futureProduct)
      .map(entertainmentPack => entertainmentPack.futureProduct.toString());

    return entertainmentPacks.map(entertainmentPack => {
      if (entertainmentPack.hasDayPass === true && futureProducts.includes(entertainmentPack.omapiId)) {
        entertainmentPack.subscribed = true;
      }
      return entertainmentPack;
    });
  }
}
